import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import StyledTableRow from "../../components/StyledTableRow";
import { getUniqueValues } from "../../util/customFunctions";
import CopyTablesObject from "../../components/xlsx-export/CopyTablesObject";

const useStyle = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: "2rem",
  },
  tableWrapper: {
    position: "relative",
    display: "inline-block",
    width: "fit-content",
    margin: "0 auto",
  },
  copyButtonContainer: {
    position: "absolute",
    top: "-30px",
    right: "0px",
    zIndex: 10,
  },
  sticky: {
    position: "sticky",
    left: -1,
    background: "white",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      width: "1.5px",
      background: "lightgrey",
    },
  },
  repHeader: {
    fontWeight: 600,
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
    width: "100px",
    textAlign: "center",
  },
  repCell: {
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
  },
  editableCell: {
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#e3f2fd",
    },
  },
  editingCell: {
    border: "0.5px solid lightgrey",
    padding: "5px",
  },
  table: {
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
  },
  mainTableContainer: {
    maxHeight: "85vh",
    maxWidth: "90vw",
    border: "0.5px solid lightgrey",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },
  totalRow: {
    backgroundColor: "#f5f5f5",
    "& td": {
      fontWeight: 700,
    },
  },
  invalidTotal: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  validTotal: {
    // backgroundColor: "#e8f5e9",
    // color: "#2e7d32",
  },
  textField: {
    width: "100%",
    "& input": {
      textAlign: "right",
      padding: "4px 8px",
      fontSize: "0.875rem",
    },
  },
}));

export default function AssumptionsByType(props) {
  const classes = useStyle();
  const tableRef = useRef();
  const tableContainerRef = useRef();

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [rowPin, setRowPin] = useState(false);
  
  // State for editing
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [localData, setLocalData] = useState([]);

  const uniqueCols = props.data
  .map(f => f.Team)
  .filter(getUniqueValues)
  .sort((a, b) => {
    if (a === "Company") return 1;   // push 'Company' down
    if (b === "Company") return -1;  // keep others above
    return a.toLowerCase().localeCompare(b.toLowerCase()); // normal sort
  }).filter(getUniqueValues);
  const uniqueTypes = props.data.map((f) => f[props.type]).filter(getUniqueValues);

  // Initialize local data
  useEffect(() => {
    setLocalData([...props.data]);
  }, [props.data]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;

    const handleOverflowCheck = () => {
      if (tableContainer) {
        setIsOverflowing(
          tableContainer.scrollWidth > tableContainer.offsetWidth
        );
      }
    };

    handleOverflowCheck();
    window.addEventListener("resize", handleOverflowCheck);

    return () => window.removeEventListener("resize", handleOverflowCheck);
  }, []);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;

    const handleScroll = () => {
      setIsSticky(tableContainer.scrollLeft > 0);
      setRowPin(tableContainer.scrollTop > 0);
    };

    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
      return () => tableContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Get value for a specific cell
  const getCellValue = (type, team) => {
    const data = localData.filter(
      (f) => f.Team === team && f[props.type] === type
    );
    return data.reduce((a, b) => a + b.Percentage, 0);
  };

  // Calculate total for a team
  const getTeamTotal = (team) => {
    const teamData = localData.filter((f) => f.Team === team);
    return teamData.reduce((sum, item) => sum + item.Percentage, 0);
  };

  // Handle cell edit start
  const handleEditStart = (type, team) => {
    const currentValue = getCellValue(type, team);
    setEditingCell({ type, team });
    setEditValue(currentValue.toFixed(2));
  };

  // Handle cell edit change
  const handleEditChange = (e) => {
    const value = e.target.value;
    // Allow numbers with up to 2 decimal places
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setEditValue(value);
    }
  };

  // Validate if new value would make total exceed 100%
  const validateTotal = (type, team, newValue) => {
    const newPercentage = parseFloat(newValue) || 0;
    const oldPercentage = getCellValue(type, team);
    const currentTotal = getTeamTotal(team);
    const newTotal = currentTotal - oldPercentage + newPercentage;
    
    return newTotal <= 100.01; // Allow small floating point error
  };

  // Handle cell save
//   const handleCellSave = () => {
//     if (!editingCell) return;
    
//     const { type, team } = editingCell;
//     const newPercentage = parseFloat(editValue) || 0;

//     // Validate
//     if (!validateTotal(type, team, editValue)) {
//     //   alert(`Total percentage for ${team} cannot exceed 100%`);
//       setEditingCell(null);
//       setEditValue("");
//       return;
//     }

//     // Update local data
//     const updatedData = localData.map((item) => {
//       if (item.Team === team && item[props.type] === type) {
//         return { ...item, Percentage: newPercentage };
//       }
//       return item;
//     });

//     setLocalData(updatedData);
    
//     // Notify parent of changes
//     if (props.onChange) {
//       props.onChange(updatedData);
//     }

//     setEditingCell(null);
//     setEditValue("");
//   };

// Handle cell save
const handleCellSave = () => {
  if (!editingCell) return;

  const { type, team } = editingCell;
  const newPercentage = parseFloat(editValue) || 0;

  // Always update — even if it exceeds total 100%
  const updatedData = localData.map((item) => {
    if (item.Team === team && item[props.type] === type) {
      return { ...item, Percentage: newPercentage };
    }
    return item;
  });

  setLocalData(updatedData);

  // Notify parent of changes
  if (props.onChange) {
    props.onChange(updatedData);
  }

  setEditingCell(null);
  setEditValue("");
};


  // Handle cell cancel
  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  // Check if total is valid (equals 100%)
  const isTotalValid = (team) => {
    const total = getTeamTotal(team);
    return Math.abs(total - 100.0) < 0.01; // Allow small floating point error
  };

  const HtmlTooltip = withStyles((theme) => ({
      tooltip: {
        backgroundColor: "rgb(255,255,255,0.8)",
        color: "black",
        fontFamily: "roboto",
        fontSize: theme.typography.pxToRem(14),
        borderRadius: "0.5rem",
        padding: "0px",
        boxShadow: "0 0.1rem 0.1rem 0",
        marginTop: "0.1rem",
      },
    }))(Tooltip);

  // Render cell content
  const renderCell = (type, team) => {
    const isEditing = editingCell?.type === type && editingCell?.team === team;
    const value = getCellValue(type, team);

    if (isEditing) {
      return (
        <TableCell className={classes.editingCell} align="right" style={{border: '2px solid black'}}>
          <TextField
            value={editValue}
            onChange={handleEditChange}
            className={classes.textField}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCellSave();
              } else if (e.key === "Escape") {
                handleCellCancel();
              }
            }}
            onBlur={handleCellSave}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </TableCell>
      );
    }

    return (
      <TableCell
        className={classes.editableCell}
        align="right"
        onClick={() => handleEditStart(type, team)}
        style={{border: '2px solid black'}}
      >
        {value.toFixed(2)}%
      </TableCell>
    );
  };

  return (
    <div className={classes.root}>
      <Grid container direction="column" alignItems="center">
        <div className={classes.tableWrapper}>
          {/* Copy button */}
          <div className={classes.copyButtonContainer}>
            <CopyTablesObject tableRef={tableRef} />
          </div>

          <TableContainer
            ref={tableContainerRef}
            className={classes.mainTableContainer}
          >
            <Table
              ref={tableRef}
              size="small"
              aria-label="data table"
              style={{ tableLayout: "auto", width: "initial" }}
            >
              <TableHead style={{ position: "sticky", top: -1, zIndex: 5 }}>
                <TableRow>
                  <TableCell
                    align="center"
                    className={
                      isSticky
                        ? `${classes.sticky} ${classes.repHeader}`
                        : classes.repHeader
                    }
                  >
                    {props.firstColumnHeader}
                  </TableCell>

                  {uniqueCols.map((col, index) => (
                     <HtmlTooltip
                                        title={
                                          <div
                                            style={{
                                              borderTop: "solid #fee599ff",
                                              borderRadius: "0.5rem",
                                              padding: "0px",
                                              margin: 0,
                                              borderWidth: "0.25rem",
                                            }}
                                          >
                                            <div className="container">
                                              <Typography
                                                style={{ padding: "0.1rem" }}
                                                className="heading-style"
                                                align="left"
                                              >{`Double-click on the cell you'd like to edit`}</Typography>
                                            </div>
                                          </div>
                                        }
                                      >
                    <TableCell
                      key={col}
                      align="center"
                      className={classes.repHeader}
                      style={{
                        background: index % 2 === 0 ? "#4471c4" : "#5b9bd5",
                        color: "white",
                      }}
                    >
                      <div>{col}</div>
                       <div style={{ background: '#fee599', padding: '0rem 0.4rem', border: '0.5px solid lightgrey', color: 'black', textAlign: 'center', display: 'inline-block', maxWidth: '50px', fontSize: '0.85rem', fontWeight: 600, marginTop: '4px' }}>
                        Edit
                      </div>
                    </TableCell>
                    </HtmlTooltip>
                  ))}

                  {uniqueCols.length === 0 && (
                    <TableCell
                      className={classes.repHeader}
                      style={{ width: 400, textAlign: "center" }}
                    >
                      No data available
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Data Rows - Editable */}
                {uniqueTypes.map((type) => (
                  <StyledTableRow key={type}>
                    <TableCell
                      className={
                        isSticky
                          ? `${classes.sticky} ${classes.table} ${classes.repCell}`
                          : `${classes.table} ${classes.repCell}`
                      }
                      align="left"
                    >
                      {type}
                    </TableCell>

                    {uniqueCols.map((col) => renderCell(type, col))}
                  </StyledTableRow>
                ))}

                {/* Total Row - Not Editable */}
                <StyledTableRow className={classes.totalRow}>
                  <TableCell
                    className={
                      isSticky
                        ? `${classes.sticky} ${classes.table} ${classes.repCell}`
                        : `${classes.table} ${classes.repCell}`
                    }
                    align="left"
                    style={{ fontWeight: 700 }}
                  >
                    Total
                  </TableCell>

                  {uniqueCols.map((col) => {
                    const total = getTeamTotal(col);
                    const isValid = isTotalValid(col);

                    return (
                      <TableCell
                        key={`total-${col}`}
                        align="right"
                        className={`${classes.repCell} ${
                          isValid ? classes.validTotal : classes.invalidTotal
                        }`}
                        style={{ fontWeight: 700 }}
                      >
                        {total.toFixed(2)}%
                      </TableCell>
                    );
                  })}
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </div>
  );
}